import type { TasklistPageJson, TasklistPageSubtask } from '@/types/tasklistPageJson';

const LOG_PREFIX = '[mapOnboardingPageSubtasksFromApi]';

/** Rows from `GET .../subtasks?...&autocomplete=true` (title + id + completion). */
export type ApiSubtaskAutocompleteRow = {
  taskId: number;
  taskTitle: string;
  completed: boolean;
};

function normTitle(s: string | undefined | null): string {
  return String(s ?? '').trim().toLowerCase();
}

function clonePageJson<T extends TasklistPageJson>(page: T): T {
  if (typeof structuredClone === 'function') {
    return structuredClone(page);
  }
  return JSON.parse(JSON.stringify(page)) as T;
}

/**
 * Merges autocomplete-shaped subtask API rows (e.g. from GET `/tasks`) into a checklist page JSON copy.
 *
 * - **Id first:** When a JSON subtask has a finite `taskId` (e.g. from `scripts/task_id_complete.py`), match
 *   `taskId` to API `taskId` before consuming that row.
 * - **Fallback:** Match each subtask `name` to API `taskTitle` (trimmed, case-insensitive).
 * - Does not mutate `pageJson`.
 * - On match: sets `taskId`, `subTaskId` (same as relay row id), and `completed` for PUT / UI merge.
 * - Unmatched JSON subtasks: `completed: false`; preserves existing fields (including static `taskId` when no match).
 * - Unmatched API rows: console warning only.
 */
export function mapOnboardingPageSubtasksFromApi<T extends TasklistPageJson>(
  pageJson: T,
  apiRows: ApiSubtaskAutocompleteRow[]
): T {
  const out = clonePageJson(pageJson);
  const list = out.subtasks;
  if (!list?.length) {
    if (apiRows.length > 0) {
      console.warn(
        `${LOG_PREFIX} API returned ${apiRows.length} subtask row(s) but page JSON has no subtasks (slug/id: ${String(pageJson.slug ?? pageJson.id ?? pageJson.title)})`
      );
    }
    return out;
  }

  const consumedApiTaskIds = new Set<number>();
  const nextSubtasks: TasklistPageSubtask[] = list.map((st) => {
    const jsonTaskId = Number(st.taskId);
    if (Number.isFinite(jsonTaskId) && jsonTaskId > 0) {
      const idMatch = apiRows.find(
        (r) => !consumedApiTaskIds.has(r.taskId) && Number(r.taskId) === jsonTaskId
      );
      if (idMatch) {
        consumedApiTaskIds.add(idMatch.taskId);
        const taskId = Number(idMatch.taskId);
        return {
          ...st,
          taskId,
          subTaskId: taskId,
          completed: Boolean(idMatch.completed),
        };
      }
    }

    const key = normTitle(st.name);
    const match =
      key.length > 0
        ? apiRows.find((r) => !consumedApiTaskIds.has(r.taskId) && normTitle(r.taskTitle) === key)
        : undefined;

    if (!match) {
      if (key.length > 0) {
        console.warn(
          `${LOG_PREFIX} No API match for subtask name "${st.name}" (page: ${String(pageJson.slug ?? pageJson.id ?? pageJson.title)})`
        );
      }
      return {
        ...st,
        completed: false,
      };
    }

    consumedApiTaskIds.add(match.taskId);
    const completed = Boolean(match.completed);
    const taskId = Number(match.taskId);
    return {
      ...st,
      taskId,
      subTaskId: taskId,
      completed,
    };
  });

  out.subtasks = nextSubtasks;

  for (const row of apiRows) {
    if (!consumedApiTaskIds.has(row.taskId)) {
      console.warn(
        `${LOG_PREFIX} No JSON subtask matched API taskTitle "${row.taskTitle}" (taskId ${row.taskId}, page: ${String(pageJson.slug ?? pageJson.id ?? pageJson.title)})`
      );
    }
  }

  return out;
}

/**
 * Parses `GET .../subtasks?...&autocomplete=true` JSON body.
 */
export function parseSubtaskAutocompleteResponse(data: unknown): ApiSubtaskAutocompleteRow[] {
  if (!Array.isArray(data)) return [];
  const out: ApiSubtaskAutocompleteRow[] = [];
  for (const row of data) {
    if (!row || typeof row !== 'object') continue;
    const o = row as Record<string, unknown>;
    const rawId = o.taskId ?? o.subTaskId ?? o.id;
    const taskId = Number(rawId);
    const taskTitle = String(o.taskTitle ?? o.title ?? '').trim();
    if (!Number.isFinite(taskId) || !taskTitle) continue;
    const completed =
      typeof o.completed === 'boolean'
        ? o.completed
        : String(o.completed).toLowerCase() === 'true' ||
          String(o.status ?? '').toUpperCase() === 'COMPLETE';
    out.push({ taskId, taskTitle, completed: Boolean(completed) });
  }
  return out;
}
